import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PawPrint, Plus } from 'lucide-react';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_DOG_VALUE = '__new_dog__';

/**
 * Dog name picker.
 * - If the customer is signed in, we fetch their saved dog profiles and render
 *   a dropdown of known names with "Add a new dog" as the last option.
 *   When "Add a new dog" is selected, a text input appears below.
 * - If the customer is not signed in (or no saved dogs exist), falls back to a
 *   plain text input so the flow isn't blocked.
 *
 * Props:
 *   value       current dog name string
 *   onChange    (newName: string) => void
 *   required    add "*" marker and required attr on fallback input
 *   label       optional label text (defaults to "Dog's Name")
 *   placeholder text input placeholder
 *   testId      data-testid prefix
 */
export const DogPicker = ({
  value,
  onChange,
  required = false,
  label = "Dog's Name",
  placeholder = "Enter your dog's name",
  testId = 'dog-picker',
}) => {
  const { customer } = useAuth();
  const [names, setNames] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [addingNew, setAddingNew] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!customer) {
      setNames([]);
      setLoaded(true);
      return;
    }
    const token = localStorage.getItem('customer_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API}/account/dog-profiles`, { headers })
      .then(res => {
        if (cancelled) return;
        const unique = Array.from(
          new Set(
            (res.data || [])
              .map(p => p.dog_profile?.dog_name?.trim())
              .filter(Boolean)
          )
        );
        setNames(unique);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [customer]);

  const useDropdown = customer && names.length > 0;
  const currentMatchesSaved = useDropdown && names.some(n => n.toLowerCase() === (value || '').toLowerCase());
  const hasValue = !!(value && value.trim());
  const showTextInput = !useDropdown || addingNew || (hasValue && !currentMatchesSaved);

  const selectValue = addingNew
    ? NEW_DOG_VALUE
    : currentMatchesSaved
      ? value
      : hasValue
        ? NEW_DOG_VALUE
        : '';

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === NEW_DOG_VALUE) {
      setAddingNew(true);
      onChange('');
    } else if (v === '') {
      setAddingNew(false);
      onChange('');
    } else {
      setAddingNew(false);
      onChange(v);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold text-[#333]">
          {label}{required && ' *'}
        </label>
      )}

      {useDropdown && (
        <div className="relative">
          <PawPrint className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select
            value={selectValue}
            onChange={handleSelect}
            className="w-full pl-10 pr-3 py-2.5 rounded-md border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent appearance-none"
            data-testid={`${testId}-select`}
          >
            <option value="">Select a dog…</option>
            {names.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value={NEW_DOG_VALUE}>➕ Add a new dog</option>
          </select>
        </div>
      )}

      {showTextInput && (
        <div className="relative">
          <PawPrint className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pl-10 bg-gray-50 border-gray-200 focus:border-[#0FA49C]"
            required={required}
            data-testid={`${testId}-input`}
          />
        </div>
      )}

      {useDropdown && addingNew && (
        <p className="text-xs text-[#999] flex items-center gap-1">
          <Plus className="w-3 h-3" /> New dog — this name will appear in your account next time.
        </p>
      )}
    </div>
  );
};

export default DogPicker;
